import { CLUSTER_CONFIG } from '@/config/market-config'
import type { YieldOpportunity } from '../types'

/**
 * Display label for a market. For elend sources this is the cluster name as
 * reported by the indexer / product registry (e.g. "HypurrFi Prime",
 * "HypurrFi Scale"), so new clusters appear automatically without editing this
 * file. When market data is missing entirely we fall back to "HypurrFi
 * Unlabeled" rather than silently misattributing to an unrelated cluster.
 */
export type MarketType = string

export const UNLABELED_MARKET_LABEL = 'HypurrFi Unlabeled'

/**
 * Static descriptions for the fixed, non-elend sources (plus the Unlabeled
 * fallback). Elend clusters supply their own description via
 * `market.description`, which is preferred when present.
 */
export const MARKET_TYPE_DESCRIPTIONS: Record<string, string> = {
  'HypurrFi Earn Vault':
    'Curated vaults that deploy capital across multiple strategies. Professional management with diversified exposure.',
  'HypurrFi Legacy':
    'Isolated lending pairs with single collateral type. Simple, direct exposure to specific asset pairs.',
  'HypurrFi Pooled':
    'Shared liquidity pool where any enabled collateral can borrow any asset. Maximum flexibility and capital efficiency.',
  [UNLABELED_MARKET_LABEL]: 'Market details are not yet available.',
}

export function getMarketType(opp: YieldOpportunity): MarketType {
  if (opp.source === 'elend') {
    return opp.market?.name ?? UNLABELED_MARKET_LABEL
  }
  if (opp.source === 'evault') return 'HypurrFi Earn Vault'
  if (opp.source === 'isolated') return 'HypurrFi Legacy'
  return 'HypurrFi Pooled'
}

/**
 * Tooltip description for an opportunity's market.
 *
 * Resolution order for elend opportunities:
 *   1. `market.description` if the indexer / `HYPEREVM_PRODUCTS` registry
 *      supplied one on the market object directly.
 *   2. `CLUSTER_CONFIG[clusterKey].details` — the curated long-form copy
 *      authored alongside the product. Always available for registered
 *      clusters, so tooltips stay meaningful even when the indexer returned
 *      the product without a description field.
 *
 * Non-elend sources (Earn / Legacy / Pooled) use the static map above, with a
 * final fallback to the Unlabeled copy for anything unregistered.
 */
export function getMarketTypeDescription(opp: YieldOpportunity): string {
  if (opp.source === 'elend') {
    if (opp.market?.description) return opp.market.description
    if (opp.market?.key) {
      const clusterKey = opp.market.key.replace(/^hypurrfi-/, '')
      const config = CLUSTER_CONFIG[clusterKey]
      if (config) return config.details
    }
  }
  const label = getMarketType(opp)
  return MARKET_TYPE_DESCRIPTIONS[label] ?? MARKET_TYPE_DESCRIPTIONS[UNLABELED_MARKET_LABEL]!
}
